import { StyleSpecification } from 'react-map-gl/mapbox';

import {
  SensorConfigurationByType,
  SensorWithSubSensorType,
  SubSensorConfigurationWithParentType,
} from './sensor/configuration.types';
import { MonitoringSensorByType } from './sensor/monitoring.types';
import { EffectiveSensorStatusEnum, ExtendedSensor, SensorTypeEnum, SpecificStateEnum } from './sensor/sensors';
import { SubSensorStatusByParentType } from './sensor/status.types';

export type BaseStyleRaster = {
  baseStyle: StyleSpecification;
  url: string;
};

export enum MapIdEnum {
  CONFIG_EDIT = 'CONFIG_EDIT',
  CONFIG_VIEW = 'CONFIG_VIEW',
  LAD = 'LAD',
  LIVE = 'LIVE',
  REPLAY = 'REPLAY',
  STATISTICS = 'STATISTICS',
}

export type OperationalMapId = Exclude<MapIdEnum, MapIdEnum.CONFIG_EDIT | MapIdEnum.CONFIG_VIEW>;

export const OperationalMapIdValues = [
  MapIdEnum.LAD,
  MapIdEnum.LIVE,
  MapIdEnum.REPLAY,
  MapIdEnum.STATISTICS,
] as const satisfies readonly OperationalMapId[];

export enum FeatureTypeEnum {
  AUTOMATON_SECTION_ICON = 'automaton-section-icon',
  AUTOMATON_SECTION_LABEL = 'automaton-section-label',
  AUTOMATON_SECTION_LINE = 'automaton-section-line',
  AUTOMATON_SECTION_POINT = 'automaton-section-point',
  ENGAGEMENT_CIRCLE = 'engagement-circle',
  ENGAGED_TARGET_ICON = 'engaged-target-icon',
  EVENT_ICON = 'event-icon',
  EXPIRED_DRONE_ICON = 'expired-drone-icon',
  DIVERSION_FATO_ICON = 'diversion-fato-icon',
  FATO_ICON = 'fato-icon',
  FLIGHT_PLAN = 'flight-plan',
  INITIALIZATION_MASKING_ZONE = 'initialization-masking-zone',
  INITIALIZATION_MASKING_ZONE_MARGIN = 'initialization-masking-zone-margin',
  LAD_CAMERA_AZIMUTH = 'lad-camera-azimuth',
  LAD_CAMERA_SECTOR = 'lad-camera-sector',
  MEASURE = 'measure',
  NEUTRAL_SECTOR = 'neutral-sector',
  NO_INIT_ZONE = 'no-init-zone',
  NO_FLY_ZONE = 'no-fly-zone',
  NO_FLY_ZONE_3D = 'no-fly-zone-3d',
  PERIMETER_CAMERA_AZIMUTH = 'perimeter-camera-azimuth',
  PERIMETER_CAMERA_LABEL = 'perimeter-camera-label',
  PERIMETER_CAMERA_SECTOR = 'perimeter-camera-sector',
  PERIMETER_LINK = 'perimeter-link',
  PERIMETER_PORTAL = 'perimeter-portal',
  PLATFORM_PERIMETER = 'platform-perimeter',
  POI_ICON = 'point-of-interest-icon',
  RADAR_COVERAGE = 'radar-coverage',
  ROUND_SECTION_LINE = 'round-section-line',
  SENSOR_ICON = 'sensor-icon',
  SENSOR_LABEL = 'sensor-label',
  STATISTIC_POINT = 'statistic-point',
  STATISTIC_TRAJECTORY = 'statistic-trajectory',
  STROBE_AZIMUTH = 'strobe-azimuth',
  STROBE_SECTOR = 'strobe',
  POLYGON_LOCATION = 'polygon-location',
  POLYGON_LOCATION_OUTLINE = 'polygon-location-outline',
  POLYGON_TRAJECTORY_OUTLINE = 'polygon-trajectory-outline',
  TARGET_CROSS = 'target-cross',
  TARGET_FLIGHT_PLAN_LINK = 'target-flight-plan-link',
  TARGET_GCS_JAMMING_AREA = 'target-gcs-jamming-area',
  TARGET_ICON = 'target-icon',
  TARGET_LINK = 'target-link',
  TARGET_TRAJECTORY = 'target-trajectory',
  TARGET_SPEED = 'target-speed',
  VERTIPORT = 'vertiport',
}

export enum LayerNameEnum {
  AUTOMATON_SECTIONS = 'automaton-sections-layer',
  AUTOMATON_SECTION_EXTREMITIES = 'automaton-section-extremities-layer',
  AUTOMATON_SECTION_ICONS = 'automaton-section-icons-layer',
  AUTOMATON_SECTION_LABELS = 'automaton-section-label-layer',
  AUTOMATON_SECTION_OUTLINES = 'automaton-section-outlines-layer',
  BASE_LAYER = 'base-layer-layer',
  DRAW_ZONE = 'draw-zone-layer',
  ENGAGED_TARGET = 'engaged-target-layer',
  ENGAGED_TARGET_CONTINGENCY = 'engaged-target-contingency-layer',
  ENGAGED_CIRCLE = 'engaged-circle-layer',
  ENGAGED_TARGET_SELECTION = 'engaged-target-selection-layer',
  ENGAGED_TARGET_SELECTION_CONTINGENCY = 'engaged-target-selection-contingency-layer',
  ENGAGED_CIRCLE_SELECTION = 'engaged-circle-selection-layer',
  EVENTS = 'events-layer',
  EXPIRED_DRONE_TRAJECTORY = 'expired-drone-trajectory-layer',
  DIVERSION_FATO_CIRCLE = 'diversion-fato-circle-layer',
  DIVERSION_FATO_SYMBOL = 'diversion-fato-symbol-layer',
  FATO = 'fato-layer',
  FLIGHT_PLAN_INTERSECTION_FILLS = 'flight-plan-intersection-fills-layer',
  FLIGHT_PLAN_INTERSECTION_OUTLINES = 'flight-plan-intersection-outlines-layer',
  FLIGHT_PLAN_FILLS = 'flight-plan-fills-layer',
  FLIGHT_PLAN_OUTLINES = 'flight-plan-outlines-layer',
  INITIALIZATION_MASKING_ZONE_FILLS = 'initialization-masking-zone-fills-layer',
  INITIALIZATION_MASKING_ZONE_MARGINS = 'initialization-masking-margins-layer',
  INITIALIZATION_MASKING_ZONE_MARGIN_OUTLINES = 'initialization-masking-margin-outlines-layer',
  INITIALIZATION_MASKING_ZONE_OUTLINES = 'initialization-masking-outlines-layer',
  LAD_CAMERA_AZIMUTHS = 'lad-camera-azimuths-layer',
  LAD_CAMERA_SECTORS = 'lad-camera-sectors-layer',
  LAD_CAMERA_SECTOR_OUTLINES = 'lad-camera-sector-outlines-layer',
  MEASURE_LINES = 'measure-lines-layer',
  MEASURE_POINTS = 'measure-points-layer',
  MEASURE_POINT_LABELS = 'measure-point-labels-layer',
  NEUTRAL_SECTOR_FILLS = 'neutral-sector-fills',
  NEUTRAL_SECTOR_STRIPES = 'neutral-sector-stripes',
  NEUTRAL_SECTOR_OUTLINES = 'neutral-sector-outlines',
  NO_FLY_ZONE_FILLS = 'no-fly-zone-fills-layer',
  NO_FLY_ZONE_OUTLINES = 'no-fly-zone-outlines-layer',
  NO_INIT_ZONE_FILLS = 'no-init-zone-fills-layer',
  NO_INIT_ZONE_OUTLINES = 'no-init-zone-outlines-layer',
  PERIMETER_CAMERA_AZIMUTHS = 'perimeter-camera-azimuths-layer',
  PERIMETER_CAMERA_LABELS = 'perimeter-camera-labels-layer',
  PERIMETER_CAMERA_SECTOR_FILLS = 'perimeter-camera-sector-fills-layer',
  PERIMETER_CAMERA_SECTOR_OUTLINES = 'perimeter-camera-sector-outlines-layer',
  PERIMETER_LINKS = 'perimeter-links-layer',
  PERIMETER_LINK_OUTLINES = 'perimeter-link-outlines-layer',
  PERIMETER_PORTALS = 'perimeter-portals-layer',
  PERIMETER_SENSOR_ICONS = 'perimeter-sensor-icons-layer',
  PERIMETER_SENSOR_LABELS = 'perimeter-sensor-labels-layer',
  PLATFORM_OUTLINES = 'platform-outlines-layer',
  PLATFORM_PERIMETER = 'platform-perimeter-layer',
  POI = 'points-of-interest-layer',
  RADAR_COVERAGE = 'radar-coverage-layer',
  ROUND_SECTIONS = 'round-sections-layer',
  SENSOR_CLUSTERS = 'sensor-clusters-layer',
  SENSOR_CLUSTER_LEAVES = 'sensor-cluster-leaves-layer',
  SENSOR_CLUSTER_OVERLAY = 'sensor-cluster-overlay-layer',
  SENSOR_ICONS = 'sensor-icons-layer',
  SENSOR_ICON_SELECTION = 'sensor-icon-selection-layer',
  SENSOR_LABELS = 'sensor-labels-layer',
  SENSOR_LABEL_SELECTION = 'sensor-label-selection-layer',
  STATISTICS_POINTS = 'statistics-points-layer',
  STATISTICS_TRAJECTORIES = 'statistics-trajectories-layer',
  STATISTIC_TRAJECTORY_ARROWS = 'statistic-trajectory-arrows-layer',
  STATISTIC_TRAJECTORY_ARROWS_OUTLINES = 'statistic-trajectory-arrows-outlines-layer',
  STATISTICS_TRAJECTORIES_OUTLINES = 'statistics-trajectories-outlines-layer',
  STROBE_AZIMUTHS = 'strobe-azimuths-layer',
  STROBE_SECTORS = 'strobe-sectors-layer',
  POLYGON_LOCATIONS = 'polygon-locations-layer',
  POLYGON_LOCATION_OUTLINES = 'polygon-location-outlines-layer',
  POLYGON_TRAJECTORY_OUTLINES = 'polygon-trajectory-outlines-layer',
  TARGETS = 'targets-layer',
  TARGETS_CONTINGENCY = 'targets-contingency-layer',
  TARGET_CROSSES = 'target-cross-layer',
  TARGET_FLIGHT_PLAN_LINKS = 'target-flight-plan-links-layer',
  TARGET_GCS_JAMMING = 'target-gcs-jamming-layer',
  TARGET_LINKS = 'target-links-layer',
  TARGET_SELECTION = 'target-selection-layer',
  TARGET_SELECTION_CONTINGENCY = 'target-selection-contingency-layer',
  TARGET_SPEED = 'target-speed-layer',
  TARGET_TRAJECTORIES = 'target-trajectories-layer',
  VERTIPORTS = 'vertiports-layer',
  VERTIPORT_OVERLAY = 'vertiport-overlay-layer',
}

export enum SourceNameEnum {
  AUTOMATON_SECTION_ICONS = 'automaton-section-icons-source',
  AUTOMATON_SECTION_LINES = 'automaton-section-lines-source',
  BASE_LAYER = 'base-layer-source',
  EVENTS = 'events-source',
  ENGAGEMENT = 'engagement-source',
  FLIGHT_PLANS = 'flight-plans-source',
  FLIGHT_PLAN_INTERSECTIONS = 'flight-plan-intersections-source',
  GCS_TARGETS = 'gcs-targets-source',
  LAD_CAMERA_COVERAGES = 'lad-camera-coverages-source',
  MEASURES = 'measures-source',
  NEUTRAL_SECTORS = 'neutral_sectors-source',
  PERIMETER_CAMERA_LABELS = 'perimeter-camera-labels-source',
  PERIMETER_CAMERA_SECTORS = 'perimeter-camera-sectors-source',
  PERIMETER_SENSORS = 'perimeter-sensors-source',
  PLATFORMS = 'platforms-source',
  POIS = 'point-of-interest-source',
  RADARS = 'radars-source',
  ROUNDS = 'rounds-source',
  SELECTION = 'selection-source',
  SENSORS = 'sensors-source',
  SENSORS_OVERLAY = 'sensors-overlay-source',
  SENSOR_LABELS = 'sensor-labels-source',
  STATISTICS = 'statistics-source',
  STROBES = 'strobes-source',
  POLYGONS = 'polygons-source',
  TARGETS = 'targets-source',
  VERTIPORTS = 'vertiports-source',
  ZONES = 'zones-source',
}

export type SensorUniqueKeys = { sensorUniqueKey: string; parentUniqueKey?: string };

export type ViewMode = '2D' | '3D';

export type MapBoxGeometry = {
  type: string;
  //coordinates MapBox : [[longitude, latitude]][]
  coordinates: [[number, number]][];
};

export type MapBoxDrawEvent = {
  id: string;
  properties: object;
  geometry: MapBoxGeometry;
};

export enum ZoneFormDisplayedFormatEnum {
  HEIGHT = 'height',
  ALTITUDE = 'altitude',
}

export const SENSORS_NOT_DISPLAYED_ON_MAP = [
  SensorTypeEnum.ARDRONIS,
  SensorTypeEnum.SENID,
  SensorTypeEnum.SKY_EYE,
  SensorTypeEnum.SPEXER,
] as const satisfies readonly SensorTypeEnum[];
export type SensorNotDisplayedOnMap = (typeof SENSORS_NOT_DISPLAYED_ON_MAP)[number];

export type SensorDisplayedOnMap = Exclude<SensorTypeEnum, SensorNotDisplayedOnMap>;
export type SensorLikeDisplayedOnMap = Exclude<ExtendedSensor, SensorNotDisplayedOnMap | 'AUTOMATON_SEGMENT'>;

export type SplitMonitoringSensors = {
  regularSensors: MonitoringSensorByType<SensorDisplayedOnMap>[];
  withSubSensors: MonitoringSensorByType<SensorWithSubSensorType>[];
};

export type SubSensorInfo<Sensor extends SensorWithSubSensorType> = {
  parentSensorConfig: SensorConfigurationByType<Sensor>;
  subSensorConfig: SubSensorConfigurationWithParentType<Sensor>;
  subSensorStatus: SubSensorStatusByParentType<Sensor> | null;
};

export type SensorLikeIconBasicInfo = {
  active: boolean;
  maintenance: boolean;
  type: SensorLikeDisplayedOnMap;
  status: EffectiveSensorStatusEnum | undefined;
  state: SpecificStateEnum | null | undefined;
};

export type FilterMode = 'regular' | 'statistics';

export type StatisticFilterColorMode = 'byMaxAlertLevel' | 'byTrajectory' | 'byMaxHeight';

export interface StatisticFilterColorItem {
  label: string;
  value: StatisticFilterColorMode;
}
